/**
 * Redirect components for migrating /coagent/* → /workspaces/:wsSlug/departments/*.
 * These preserve query params and handle old slug → new dept/team mapping.
 */
import { Navigate, useParams, useSearchParams } from "react-router-dom";
import { getStoredDefaultWorkspace } from "@/hooks/useDefaultWorkspace";
import {
  resolveLegacySlugPath,
  resolveLegacyAgentDetailPath,
} from "@/features/department/lib/departmentRoutes";

function logRouteWarning(
  warningCode: string | undefined,
  context: Record<string, string | undefined>,
) {
  if (!warningCode) return;
  console.warn("[routes][legacy-redirect]", warningCode, context);
}

function getWsSlug(): string | undefined {
  return getStoredDefaultWorkspace()?.slug || undefined;
}

/** /coagent/:agentSlug → /workspaces/:wsSlug/departments/:dept/:team */
export function CoAgentSlugRedirect() {
  const { agentSlug } = useParams<{ agentSlug: string }>();
  const [searchParams] = useSearchParams();
  const qs = searchParams.toString();
  const wsSlug = getWsSlug();
  const resolution = resolveLegacySlugPath(agentSlug, wsSlug);
  logRouteWarning(resolution.warningCode, { agentSlug });
  const dest = qs ? `${resolution.path}?${qs}` : resolution.path;

  return <Navigate to={dest} replace />;
}

/** /coagent/agent/:agentId → /workspaces/:wsSlug/departments/:dept (with ?flow= preserved) */
export function AgentDetailRedirect() {
  const { agentId } = useParams<{ agentId: string }>();
  const [searchParams] = useSearchParams();
  const flow = searchParams.get("flow") ?? undefined;
  const wsSlug = getWsSlug();

  const resolution = resolveLegacyAgentDetailPath({
    agentId,
    flowSlug: flow,
    wsSlug,
  });
  logRouteWarning(resolution.warningCode, { agentId, flow });
  return <Navigate to={resolution.path} replace />;
}
